

import { IconSquare, IconSquareCheckFilled, IconSquareMinus } from "@tabler/icons-react";

/** `body-1-regular` line box in px — the row height a single label line occupies. */
const LABEL_LINE_HEIGHT = 24;

type CheckboxWithValidationProps = {
  children?: React.ReactNode;
  isChecked: boolean;
  isIndeterminate?: boolean;
  isInvalid?: boolean;
  disabled?: boolean;
  size?: number;
  /**
   * Opt-in for labels that wrap: keeps the box optically centred on the FIRST
   * label line instead of drifting to the middle of the whole text block.
   */
  alignStart?: boolean;
  /**
   * With `alignStart`: the line height (px) of the label's FIRST line, when the
   * label is a block in a smaller type than `body-1` (e.g. `body-3`, 18 px) —
   * the box centres on that line instead of a 24 px one. Default: body-1.
   */
  labelLineHeight?: number;
  /** The next state. At least one of this and `onCheckChangeEvent` is needed
   *  for the box to do anything; both fire from one toggle. */
  onCheckChangeAction?: (isChecked: boolean) => void;
  /**
   * The next state AND the event that caused it — for callers that care HOW
   * the box was toggled. The guest list reads `shiftKey` to extend a range
   * from the last plain click (the mouse click or the Space/Enter keydown).
   */
  onCheckChangeEvent?: (
    isChecked: boolean,
    event: React.MouseEvent | React.KeyboardEvent,
  ) => void;
  testId?: string;
  /**
   * Id of the element that labels this checkbox, for the cases where the label
   * cannot live in `children` — e.g. a consent sentence containing links, which
   * must stay OUTSIDE the `role="checkbox"` subtree to remain reachable.
   */
  ariaLabelledBy?: string;
  /**
   * Accessible name for a box whose label is neither in `children` nor in an
   * element of its own to point at — the guest list's group-header box sits
   * in a different cell than the group's title, and the name should say what
   * the control DOES ("Select group Acme"), not repeat the heading.
   * `aria-labelledby` wins when both are given.
   */
  ariaLabel?: string;
  /** `aria-required` — a box that must be ticked before the form can go. */
  ariaRequired?: boolean;
  /**
   * `aria-invalid` — set it only once the user tried to proceed without the
   * tick; `isInvalid` stays the visual half.
   */
  ariaInvalid?: boolean;
  /** Id of the element explaining the error (pair with `ariaInvalid`). */
  ariaDescribedBy?: string;
  /** DOM id of the `role="checkbox"` element, for callers that address it by id. */
  id?: string;
  /**
   * The `role="checkbox"` element itself, so a form can move focus to the first
   * box the user left unticked (React 19: `ref` is an ordinary prop here).
   */
  ref?: React.Ref<HTMLDivElement>;
};

export function CheckboxWithValidation({
  children,
  isChecked,
  isIndeterminate,
  isInvalid,
  disabled = false,
  size = 20,
  alignStart = false,
  labelLineHeight = LABEL_LINE_HEIGHT,
  onCheckChangeAction,
  onCheckChangeEvent,
  testId,
  ariaLabelledBy,
  ariaLabel,
  ariaRequired,
  ariaInvalid,
  ariaDescribedBy,
  id,
  ref,
}: CheckboxWithValidationProps) {
  const toggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    const next = isIndeterminate ? false : !isChecked;
    onCheckChangeAction?.(next);
    onCheckChangeEvent?.(next, e);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    // Clicking the box, the icon or the label text toggles — that is the whole
    // point of wrapping children. Clicking (or keyboard-activating) a nested
    // INTERACTIVE element must not: browsers fire a click when Enter activates a
    // link, and that click bubbles here. Without this the consent screens would
    // tick their box whenever the user opened the Terms.
    //
    // Deliberately not `e.target !== e.currentTarget` as in the key handler —
    // that would also swallow clicks on the icon and the label, which must work.
    if ((e.target as HTMLElement).closest("a,button,input,select,textarea")) return;
    e.stopPropagation();
    toggle(e);
  };

  /**
   * A shift-click means "extend the range" to the callers that read the
   * event; to the browser it means "extend the text selection" from the last
   * click, which sweeps a highlight across every row in between. Swallowed on
   * mousedown — the click itself still fires and still toggles.
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.shiftKey) e.preventDefault();
  };

  /**
   * Keyboard operation. This element carries `role="checkbox"` and is in the tab
   * order, so it MUST respond to Space (and Enter, which users try anyway).
   *
   * `e.target !== e.currentTarget` is not optional: keydown bubbles, and several
   * call sites nest interactive children. Without it, activating a nested link
   * or button would also toggle the box — on the signup consent screens that
   * would have meant "I opened the terms and it accepted them for me".
   *
   * `preventDefault` on Space stops the page scrolling on every toggle;
   * `stopPropagation` keeps row-level handlers from firing twice.
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.target !== e.currentTarget) return;
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();
    toggle(e);
  };

  return (
    <div
      ref={ref}
      id={id}
      className={`flex ${alignStart ? "items-start" : "items-center"} rounded-xs focus-ring ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      // Unconditional. Tying an ARIA role to the presence of a TEST id meant
      // assistive tech saw a checkbox only where someone happened to want a test
      // hook — 82 of 103 call sites announced nothing at all.
      role="checkbox"
      tabIndex={disabled ? -1 : 0}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      aria-checked={isIndeterminate ? "mixed" : isChecked}
      aria-disabled={disabled || undefined}
      aria-required={ariaRequired || undefined}
      aria-invalid={ariaInvalid || undefined}
      aria-describedby={ariaDescribedBy}
    >
      <div
        className={`relative mr-1 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
        style={
          alignStart
            ? { marginTop: Math.max(0, (labelLineHeight - size) / 2) }
            : undefined
        }
      >
        {isIndeterminate ? (
          <IconSquareMinus size={size} className="font-primary" />
        ) : isChecked ? (
          <IconSquareCheckFilled size={size} className="font-primary" />
        ) : (
          <IconSquare size={size} className="font-quaternary" stroke={1.5} />
        )}
      </div>
      {children && (
        <span
          className={`body-1-regular ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${
            isInvalid ? "font-error" : ""
          }`}
        >
          {children}
        </span>
      )}
    </div>
  );
}
